/**
 * <LandingTierCards /> — the two side-by-side entry cards that live in the
 * public landing hero. Self-explanatory descriptions so a visitor knows
 * exactly which door to enter without a quiz.
 *
 * Card A — Continuity System: paid lower-fee gateway tier.
 *   CTA: "Get Started" (routes to signup with gateway=true intent).
 *
 * Card B — Portfolio Clarity: qualification-gated top tier that includes
 *   everything in Continuity System.
 *   CTA: "Request Invitation" (opens the existing AccessRequestModal).
 *
 * Wrong-button rescue: each card carries a small link to the OTHER card's
 * action, so a visitor who taps the wrong button can pivot with one click.
 *
 * Props:
 *   - handleGetStarted:   () => void   (existing prop, opens signup/dashboard)
 *   - handleRequestInvitation: () => void   (opens AccessRequestModal)
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowRight, Check, Sparkles, Shield, Lock } from 'lucide-react';
import { isBetaActive } from '../../utils/betaMode';

const CONTINUITY_FEATURES = [
  'Encrypted personal record of every asset you own',
  'Trusted-contact & document Continuity System',
  'Access for your nominated contacts when needed',
];

const PREMIUM_FEATURES = [
  'Everything in Continuity System',
  'Asset allocation & performance analytics',
  'Tax data pack for your accountant',
  'Net worth, insights & portfolio sharing',
];

// Session 284: existing subscribers no longer see the beta lock on the Home
// page tier cards either. The two tier sets mirror the mapping used server-side
// (premium_continuity is a grandfathered top-tier alias for premium_portfolio).
const PORTFOLIO_TIERS = new Set(['premium_portfolio', 'premium_continuity']);
const CONTINUITY_TIER = 'portfolio_continuity';

// Soft "Your Current Plan" badge — non-clickable status marker.
const CurrentPlanBadge = ({ testid, tone }) => (
  <div
    data-testid={testid}
    className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full border-2 font-semibold py-3 ${tone}`}
  >
    <Check className="w-4 h-4" strokeWidth={3} />
    Your Current Plan
  </div>
);

// Cross-tier action — routes existing subscribers to Settings → Subscription.
const CrossTierLink = ({ testid, label, tone }) => (
  <Link
    to="/settings?tab=subscription"
    data-testid={testid}
    className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full font-semibold py-3 shadow-md hover:shadow-lg transition-all ${tone}`}
  >
    {label}
    <ArrowRight className="w-4 h-4" />
  </Link>
);

export const LandingTierCards = ({ handleGetStarted, handleRequestInvitation, pricing }) => {
  const betaActive = isBetaActive();
  const currentTier = pricing?.current_tier || null;
  const onPortfolioTier = PORTFOLIO_TIERS.has(currentTier);
  const onContinuityTier = currentTier === CONTINUITY_TIER;
  return (
  <div
    className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6"
    data-testid="landing-tier-cards"
  >
    {/* ── Continuity System — the gateway ─────────────────────────── */}
    <div
      className="relative rounded-2xl border-2 border-blue-600 bg-linear-to-br from-blue-50/60 to-white p-6 sm:p-7 flex flex-col shadow-md hover:shadow-xl transition-shadow"
      data-testid="landing-continuity-card"
    >
      <div className="absolute -top-3 right-6 bg-blue-600 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full shadow-md">
        Gateway
      </div>
      <div className="flex items-center gap-2 text-blue-700 mb-2">
        <Shield className="w-5 h-5" />
        <span className="text-xs font-semibold uppercase tracking-wide">Self-Serve</span>
      </div>
      <h3 className="text-xl sm:text-2xl font-serif font-bold text-slate-900">
        Continuity System
      </h3>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">
        A private record of every asset you own — clear for you today,
        and quietly ready for the people you trust if you&rsquo;re ever unavailable.
      </p>
      <ul className="mt-4 space-y-2 flex-1">
        {CONTINUITY_FEATURES.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
            <Check className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" strokeWidth={3} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {onContinuityTier ? (
        <CurrentPlanBadge
          testid="landing-continuity-current-plan"
          tone="border-blue-400 bg-white text-blue-800"
        />
      ) : onPortfolioTier ? (
        <CrossTierLink
          testid="landing-continuity-downgrade-link"
          label="Switch to Continuity System"
          tone="bg-white border-2 border-blue-600 text-blue-700 hover:bg-blue-50"
        />
      ) : (
        <Button
          onClick={betaActive ? undefined : handleGetStarted}
          disabled={betaActive}
          className={`mt-6 w-full font-semibold rounded-full py-3 shadow-md transition-all ${
            betaActive
              ? 'bg-slate-200 text-slate-500 hover:bg-slate-200 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg'
          }`}
          data-testid="landing-continuity-cta"
        >
          {betaActive ? (
            <>
              <Lock className="mr-2 w-4 h-4" /> Available after Beta
            </>
          ) : (
            <>
              Get Started <ArrowRight className="ml-2 w-4 h-4" />
            </>
          )}
        </Button>
      )}
      {betaActive && !onContinuityTier && !onPortfolioTier && (
        <p
          className="mt-2 text-[11px] text-slate-500 text-center leading-relaxed"
          data-testid="landing-continuity-beta-note"
        >
          Self-serve signup opens after the beta.
        </p>
      )}
      <Link
        to="/pricing-layers"
        className="mt-3 text-xs text-slate-500 hover:text-blue-700 underline underline-offset-2 transition-colors self-center"
        data-testid="landing-continuity-compare-link"
      >
        See full comparison →
      </Link>
      {!betaActive && (
        <button
          type="button"
          onClick={handleRequestInvitation}
          className="mt-1 text-xs text-slate-500 hover:text-emerald-700 underline underline-offset-2 transition-colors self-center"
          data-testid="landing-continuity-rescue-link"
        >
          Meant to Request Invitation for Portfolio Clarity? →
        </button>
      )}
    </div>

    {/* ── Portfolio Clarity — the top tier ───────────────────────────── */}
    <div
      className="relative rounded-2xl border-2 border-emerald-600 bg-linear-to-br from-emerald-50/60 to-white p-6 sm:p-7 flex flex-col shadow-md hover:shadow-xl transition-shadow"
      data-testid="landing-premium-card"
    >
      <div className="absolute -top-3 right-6 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full shadow-md">
        Premium
      </div>
      <div className="flex items-center gap-2 text-emerald-700 mb-2">
        <Sparkles className="w-5 h-5" />
        <span className="text-xs font-semibold uppercase tracking-wide">By Invitation</span>
      </div>
      <h3 className="text-xl sm:text-2xl font-serif font-bold text-slate-900">
        Portfolio Clarity
      </h3>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">
        Everything in Continuity System, plus the full analytical view —
        allocation, performance, tax data, net worth and portfolio sharing.
      </p>
      <ul className="mt-4 space-y-2 flex-1">
        {PREMIUM_FEATURES.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
            <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" strokeWidth={3} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {onPortfolioTier ? (
        <CurrentPlanBadge
          testid="landing-premium-current-plan"
          tone="border-emerald-400 bg-white text-emerald-800"
        />
      ) : onContinuityTier ? (
        <CrossTierLink
          testid="landing-premium-upgrade-link"
          label="Upgrade to Portfolio Clarity"
          tone="bg-emerald-600 hover:bg-emerald-700 text-white"
        />
      ) : (
        <Button
          onClick={betaActive ? undefined : handleRequestInvitation}
          disabled={betaActive}
          className={`mt-6 w-full font-semibold rounded-full py-3 shadow-md transition-all ${
            betaActive
              ? 'bg-slate-200 text-slate-500 hover:bg-slate-200 cursor-not-allowed'
              : 'bg-emerald-600 hover:bg-emerald-700 text-white hover:shadow-lg'
          }`}
          data-testid="landing-premium-cta"
        >
          {betaActive ? (
            <>
              <Lock className="mr-2 w-4 h-4" /> Available after Beta
            </>
          ) : (
            <>
              Request Invitation <ArrowRight className="ml-2 w-4 h-4" />
            </>
          )}
        </Button>
      )}
      {betaActive && !onContinuityTier && !onPortfolioTier && (
        <p
          className="mt-2 text-[11px] text-slate-500 text-center leading-relaxed"
          data-testid="landing-premium-beta-note"
        >
          Invitation requests open after the beta.
        </p>
      )}
      <Link
        to="/pricing-layers"
        className="mt-3 text-xs text-slate-500 hover:text-emerald-700 underline underline-offset-2 transition-colors self-center"
        data-testid="landing-premium-compare-link"
      >
        See full comparison →
      </Link>
      {!betaActive && (
        <button
          type="button"
          onClick={handleGetStarted}
          className="mt-1 text-xs text-slate-500 hover:text-emerald-700 underline underline-offset-2 transition-colors self-center"
          data-testid="landing-premium-rescue-link"
        >
          Prefer to Get Started with Continuity System? →
        </button>
      )}
    </div>
  </div>
  );
};

export default LandingTierCards;
